import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { ConditionalLink } from "components/conditionalLink";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { AlreadyRegisteredModal } from "components/SalesTax/AlreadyRegisteredModal";
import { NudgeAddFilingToCartModal } from "components/SalesTax/NudgeAddFilingToCartModal";
import { SalesTaxComplianceBanners } from "components/SalesTax/SalesTaxComplianceBanners";
import { StatePhysicalPresenceModal } from "components/SalesTax/StatePhysicalPresenceModal";
import { useInvoiceSalesTaxCompliance } from "components/SalesTax/useInvoiceSalesTaxCompliance";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { INKLE_WEBSITE_URL } from "constants/base";

const SALES_TAX_REGISTRATION_HELP_LINK = `${INKLE_WEBSITE_URL}sales-tax/register-for-sales-tax-on-inkle`;

type InvoiceSalesTaxComplianceProps = {
  groupId: string;
  entityId: string;
  buyerStateName?: string | null;
};

export const InvoiceSalesTaxCompliance = ({
  groupId,
  entityId,
  buyerStateName,
}: InvoiceSalesTaxComplianceProps) => {
  const { isAdmin } = useRoleBasedView();

  const {
    nexusComplianceInfo,
    confirmNoPhysicalPresence,
    isConfirmingNoPresence,
    registerNow,
    isRegisteringNow,
    viewFiling,
    recalculate,
    showExposedComplianceBanner,
    showApproachingComplianceBanner,
    showCartPendingBanner,
    showPendingRegistrationBanner,
    showFilingInProgressBanner,
    showNoTaxRequiredBanner,
    dismissApproachingBanner,
  } = useInvoiceSalesTaxCompliance({ entityId, groupId, buyerStateName });

  const alreadyRegisteredModal = useModal();
  const physicalNexusModal = useModal();
  const nudgeAddFilingModal = useModal();

  const openCartInNewTab = () => {
    const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
    window.open(cartUrl, "_blank");
  };

  return (
    <>
      {showNoTaxRequiredBanner && (
        <Banner
          type="info"
          title={`Do you have a physical presence in ${nexusComplianceInfo?.stateName}?`}
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <span>
                {`Your sales are below the registration threshold in ${nexusComplianceInfo?.stateName}, so no sales tax needs to be collected based on economic nexus alone. If you have an office, employee, or inventory there, you may still need to register — confirm this so we can calculate the correct tax. `}
                <ConditionalLink
                  to={SALES_TAX_REGISTRATION_HELP_LINK}
                  className="t-text-text-30 !t-underline"
                >
                  Learn how it works
                </ConditionalLink>
              </span>
              <div className="t-flex t-items-center t-gap-3">
                <Button
                  customType="secondary"
                  size="small"
                  type="button"
                  disabled={isConfirmingNoPresence}
                  onClick={physicalNexusModal.open}
                >
                  Yes, I have
                </Button>
                <Button
                  customType="link"
                  size="small"
                  type="button"
                  isLoading={isConfirmingNoPresence}
                  disabled={isConfirmingNoPresence}
                  onClick={() => void confirmNoPhysicalPresence()}
                >
                  No, I don&apos;t
                </Button>
              </div>
            </div>
          }
        />
      )}
      <SalesTaxComplianceBanners
        showExposedBanner={showExposedComplianceBanner}
        showApproachingBanner={showApproachingComplianceBanner}
        showCartPendingBanner={showCartPendingBanner}
        showPendingRegistrationBanner={showPendingRegistrationBanner}
        showFilingInProgressBanner={showFilingInProgressBanner}
        stateName={nexusComplianceInfo?.stateName}
        isRegisteringNow={isRegisteringNow}
        onDismissApproachingBanner={dismissApproachingBanner}
        onRegisterNow={() => void registerNow()}
        onAlreadyRegistered={alreadyRegisteredModal.open}
        onGoToCart={openCartInNewTab}
        onViewFiling={viewFiling}
      />
      {alreadyRegisteredModal.isOpen && nexusComplianceInfo?.stateId && (
        <AlreadyRegisteredModal
          isOpen={alreadyRegisteredModal.isOpen}
          onClose={alreadyRegisteredModal.close}
          entityId={entityId}
          stateId={nexusComplianceInfo.stateId}
          stateName={nexusComplianceInfo.stateName}
          onSuccess={() => void recalculate()}
          onRegistered={nudgeAddFilingModal.open}
        />
      )}
      <NudgeAddFilingToCartModal
        isOpen={nudgeAddFilingModal.isOpen}
        onClose={nudgeAddFilingModal.close}
        entityId={entityId}
        groupId={groupId}
        stateId={nexusComplianceInfo?.stateId ?? ""}
        stateName={nexusComplianceInfo?.stateName ?? ""}
      />
      {physicalNexusModal.isOpen && nexusComplianceInfo?.stateId && (
        <StatePhysicalPresenceModal
          isOpen={physicalNexusModal.isOpen}
          onClose={physicalNexusModal.close}
          entityId={entityId}
          stateId={nexusComplianceInfo.stateId}
          stateName={nexusComplianceInfo.stateName}
          onSuccess={() => void recalculate()}
        />
      )}
    </>
  );
};
